import { useMemo, useRef, useState } from 'react';
import {
  withStyles,
  createTableProps,
  Tooltip,
  MaterialSymbol,
  formattedCurrency,
  ClientAccount,
  Box,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
} from 'vopay';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './useTableConfig.module.scss';
import Chip from './Chip/Chip';
import TransactionStatus from './TransactionStatus/TransactionStatus';
import useClientAccountList from './useClientAccountList';

const cn = withStyles(styles);

export default function useTableConfig(accounts: ClientAccount[]) {
  const [emailOrPhone, setEmailOrPhone] = useState<'email' | 'phone'>('email');
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });

  const {
    account: { country },
    hostPaths,
  } = useClientAccountList();

  return createTableProps({
    id: 'client-accounts',
    zebra: true,
    rows: accounts.map((c) => ({
      id: c.ClientAccountID,
      data: c,
    })),
    columns: [
      {
        id: 'ClientName',
        header: t('name', { defaultValue: 'Name' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => (
          <Link to={hostPaths.accountDetail(data.ClientAccountID)} className={cn('link')}>
            {data.ClientName}
          </Link>
        ),
      },
      {
        id: 'Email',
        header: (
          <Box gap="sm" ai="center">
            {t('email', { defaultValue: 'Email' })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'email',
        template: ({ data }) => data.EmailAddress,
        onHeaderClick: () => {
          setEmailOrPhone('phone');
        },
      },
      {
        id: 'PhoneNumber',
        header: (
          <Box gap="sm" ai="center">
            {t('phone', { defaultValue: 'Phone Number' })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'phone',
        template: ({ data }) => formatPhoneNumber(data.PhoneNumber),
        onHeaderClick: () => {
          setEmailOrPhone('email');
        },
      },
      {
        id: 'type',
        header: t('clientAccountType', { defaultValue: 'Client Account Type' }),
        align: 'left',
        template: ({ data }) =>
          data.IsBusiness
            ? t('ClientAccountType.business', { defaultValue: 'Business' })
            : t('ClientAccountType.individual', { defaultValue: 'Individual' }),
      },
      {
        id: 'limits',
        header: t('limits', { defaultValue: 'Limits' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => <Limits country={country} limit={data.TransactionLimit} balances={data.Balances} />,
      },
      {
        id: 'Balances',
        header: t('balances', { defaultValue: 'Balances' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => <Balances country={country} balances={data.Balances} />,
      },
      {
        id: 'Labels',
        header: t('labels', { defaultValue: 'Labels' }),
        align: 'left',
        template: ({ data }) => <Labels labels={data.Labels} />,
      },
      {
        id: 'status',
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.IsActive ? 'active' : 'inactive')}
              label={capitalizeFirstLetterOfWords(
                data.IsActive
                  ? t('Status.active', { defaultValue: 'Active' })
                  : t('Status.inactive', { defaultValue: 'Inactive' }),
              )}
            />
          </Box>
        ),
        header: t('status', { defaultValue: 'Status' }),
      },
    ],
  });
}

function Labels({ labels }: { labels: string[] }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const ref = useRef<HTMLDivElement | null>(null);

  const overflowing = useMemo(() => {
    if (!ref.current) return false;

    return ref.current.scrollHeight > ref.current.clientHeight;
  }, [ref.current]);

  if (!labels.length) return <>-</>;

  return (
    <div className={cn('labelsContainer')}>
      <div ref={ref} className={cn('labels')}>
        {[...labels].map((label, idx) => (
          <Chip key={label + idx} label={label} className={cn('chip')} />
        ))}
      </div>

      {overflowing && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('allLabels', { defaultValue: 'All Labels' })}</h3>

              <ul>
                {[...labels].map((label, idx) => (
                  <li key={label + idx}>
                    <Chip label={label} className={cn('label')} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

function Limits({
  limit,
  balances,
  country,
}: {
  limit: string;
  balances?: { Currency: string; Balance: number }[];
  country: string;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) {
    return (
      <div>
        {defaultCurrency} {formattedCurrency(limit)}
      </div>
    );
  }

  return (
    <div className={cn('limitsCell')}>
      {balances.slice(0, 2).map((b) => (
        <div key={b.Currency || defaultCurrency}>
          {b.Currency || defaultCurrency} {formattedCurrency(limit.toString())}
        </div>
      ))}

      {balances.length > 2 && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('Tooltip.transactionLimits', { defaultValue: 'Transaction Limits' })}</h3>

              <ul>
                {balances.map((b, idx) => (
                  <li key={(b.Currency || defaultCurrency) + idx}>
                    <Chip
                      label={`${b.Currency || defaultCurrency} ${formattedCurrency(limit)}`}
                      className={cn('label')}
                    />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

function Balances({ balances, country }: { balances?: { Currency: string; Balance: number }[]; country: string }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) return <>-</>;

  return (
    <div className={cn('limitsCell')}>
      {balances.slice(0, 2).map((b) => (
        <div key={b.Currency || defaultCurrency}>
          {b.Currency || defaultCurrency} {formattedCurrency(b.Balance.toString())}
        </div>
      ))}

      {balances.length > 2 && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('balances', { defaultValue: 'Balances' })}</h3>

              <ul>
                {balances.map((b, idx) => (
                  <li key={(b.Currency || defaultCurrency) + idx}>
                    <Chip
                      label={`${b.Currency || defaultCurrency} ${formattedCurrency(b.Balance.toString())}`}
                      className={cn('label')}
                    />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check the length of the cleaned number
  if (cleaned.length < 10 || cleaned.length > 12) {
    // If the length is invalid, return the original input
    return phoneNumber;
  }

  let countryCode = '1'; // Default country code
  let localNumber = cleaned;

  if (cleaned.length === 11) {
    // If the length is 11, assume the first digit is the country code

    countryCode = cleaned[0];
    localNumber = cleaned.slice(1);
  } else if (cleaned.length === 12) {
    // If the length is 12, assume the first two digits are the country code
    countryCode = cleaned.slice(0, 2);
    localNumber = cleaned.slice(2);
  }

  // Format the local number
  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);

  if (match) {
    return `+${countryCode} (${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber;
}
